import React from 'react';
import { useQuery } from '@tanstack/react-query';
import ChatAvatar from '../../chat/ChatAvatar';
import { getPoints } from '../../../api/me';
import { useLeaderboard } from '../../../hooks/learner/useLeaderboard';
import { useCurrentChatIdentity } from '../../../hooks/chat/useCurrentChatIdentity';

/**
 * Mirrors Figma node `9261:733241`. Shows the viewer's own points in a
 * mini "org-chart node" treatment — avatar popped above a grey card
 * that holds name + Points X.
 */
export default function CurrentScoreCard() {
  const identity = useCurrentChatIdentity();
  const { currentRank } = useLeaderboard(50);
  const { data: points } = useQuery({
    queryKey: ['me', 'gamification', 'points'],
    queryFn: getPoints,
  });

  const balance =
    currentRank?.balance ??
    (points?.enabled !== false ? (points?.balance ?? 0) : null);

  const name =
    [currentRank?.first_name, currentRank?.last_name].filter(Boolean).join(' ').trim() ||
    identity.fullName ||
    'You';

  const avatarUrl = currentRank?.avatar_url ?? identity.avatarUrl;

  return (
    <section
      className="w-full lg:w-[205px] shrink-0 flex flex-col items-center gap-6"
      aria-label="Your Current Points"
    >
      <p className="text-[15px] font-bold text-text-primary text-center leading-snug">
        Your Current Points
      </p>
      <div className="relative pt-5 w-[160px]">
        <div className="absolute left-1/2 top-0 -translate-x-1/2 z-10 ring-4 ring-surface-primary rounded-full">
          <ChatAvatar src={avatarUrl} name={name} size={40} />
        </div>
        <div className="bg-[rgba(189,188,201,0.16)] dark:bg-white/[0.04] rounded-xl px-4 pb-6 pt-10 flex flex-col items-center gap-1 text-center">
          <p className="text-sm font-semibold text-text-primary truncate w-full">
            {name}
          </p>
          <p className="text-xs text-text-secondary tabular-nums">
            Points {balance == null ? '—' : balance.toLocaleString()}
          </p>
        </div>
      </div>
    </section>
  );
}
